/**
 * Fathom Analytics Event Tracking Configuration
 *
 * Event Naming Convention: page_section_element_action
 *
 * - page: The page or main section (e.g., 'scans', 'pricing', 'trial')
 * - section: The specific section within the page (e.g., 'hero', 'how-to', 'features')
 * - element: The UI element being interacted with (e.g., 'button', 'link', 'icon')
 * - action: The action being performed (e.g., 'click', 'copy', 'submit')
 *
 * Example: 'scans_how-to_gradle_icon_click'
 *
 * To add a new event:
 * 1. Add the event to the appropriate page object below
 * 2. Use the typed event name in your component with trackFathomEvent function
 * 3. Follow the naming convention strictly for consistency
 */

type FathomEventStructure = {
  [page: string]: {
    [section: string]: {
      [element: string]: {
        [action: string]: string;
      };
    };
  };
};

export const FATHOM_EVENTS = {
  scans: {
    'how-to': {
      gradle_icon: {
        click: 'scans_how-to_gradle_icon_click',
      },
      maven_icon: {
        click: 'scans_how-to_maven_icon_click',
      },
      sbt_icon: {
        click: 'scans_how-to_sbt_icon_click',
      },
      instructions: {
        copy: 'scans_how-to_instructions_copy',
        click: 'scans_how-to_instructions_click',
      },
      quickstart_link: {
        click: 'scans_how-to_quickstart_link_click',
      },
    },
  },
  resources: {
    'ebooks-and-whitepapers': {
      gen_ai_form: {
        submit: 'resources_ebooks-and-whitepapers_gen-ai_form_submit',
      },
      flaky_tests_form: {
        submit: 'resources_ebooks-and-whitepapers_flaky-tests_form_submit',
      },
    },
  },
  'review-terms': {
    hero: {
      form: {
        submit: 'review-terms_hero_form_submit',
      },
    },
  },
  develocity: {
    'develocity-360': {
      agentic_ai: {
        video_clicked: 'develocity-360-agentic-ai-video-clicked',
      },
      contact_us: {
        submit: 'develocity-360-contact-us-form-submit',
      },
      request_more_info: {
        click: 'develocity-360-request-more-info-click',
      },
      need_help: {
        read_documentation_click: 'develocity-360-need-help-read-documentation-click',
      },
    },
    analytics: {
      hero: {
        video_clicked: 'analytics_hero_video-clicked',
      },
      'post-hero': {
        tour_the_product_clicked: 'analytics_post-hero_tour-the-product-click',
      },
      'benefits-tabs': {
        individuals_tab_click: 'analytics_benefits-tabs_individuals-click',
        teams_tab_click: 'analytics_benefits-tabs_teams-click',
        organizations_tab_click: 'analytics_benefits-tabs_organizations-click',
      },
      'learn-more': {
        read_blog_post_click: 'analytics_learn-more_read-blog-post-click',
        watch_video_click: 'analytics_learn-more_watch-video-click',
        learn_more_click: 'analytics_learn-more_learn-more-click',
      },
    },
    'performance-insights': {
      'post-hero': {
        tour_the_product_clicked: 'performance-insights_post-hero_tour-the-product-click',
      },
      'learn-more': {
        take_the_tour_click: 'performance-insights_learn-more_take-the-tour-click',
        explore_the_data_click: 'performance-insights_learn-more_explore-the-data-click',
        take_the_course_click: 'performance-insights_learn-more_take-the-course-click',
      },
      'need-help': {
        read_documentation_click: 'performance-insights_need-help_read-documentation-click',
      },
    },
    'provenance-governor': {
      hero: {
        learn_more_button_click: 'develocity_provenance-governor_hero_learn-more-button_click',
        get_in_touch_button_click: 'develocity_provenance-governor_hero_get-in-touch-button_click',
      },
      contact_us: {
        submit: 'provenance-governor-contact-us-form-submit',
      },
    },
    observe: {
      hero: {
        cta_clicked: 'develocity_observe_hero_cta-clicked',
      },
      features: {
        performance_insights_cta_click: 'develocity_observe_features_performance-insights-cta-click',
        analytics_cta_click: 'develocity_observe_features_analytics-cta-click',
      },
      testimonial: {
        watch_video_click: 'develocity_observe_testimonial_watch-video-click',
      },
    },
    troubleshoot: {
      hero: {
        cta_clicked: 'develocity_troubleshoot_hero_cta-clicked',
      },
      features: {
        build_scan_cta_click: 'develocity_troubleshoot_features_build-scan-cta-click',
        flaky_test_detection_cta_click: 'develocity_troubleshoot_features_flaky-test-detection-cta-click',
        failure_analytics_cta_click: 'develocity_troubleshoot_features_failure-analytics-cta-click',
      },
    },
    accelerate: {
      hero: {
        cta_clicked: 'develocity_accelerate_hero_cta-clicked',
      },
      features: {
        build_cache_cta_click: 'develocity_accelerate_features_build-cache-cta-click',
        predictive_test_selection_cta_click: 'develocity_accelerate_features_predictive-test-selection-cta-click',
        test_distribution_cta_click: 'develocity_accelerate_features_test-distribution-cta-click',
      },
      testimonial: {
        get_the_story: 'develocity_accelerate_testimonial_get-the-story',
      },
    },
    'contact-us': {
      hero: {
        form_submited: 'develocity_contact-us_hero_form-submited',
      },
    },
  },
  solutions: {
    'generative-ai': {
      hero: {
        request_trial_clicked: 'solutions_generative-ai_hero_request_trial_clicked',
        get_whitepaper_clicked: 'solutions_generative-ai_hero_get_whitepaper_clicked',
      },
      acceleration_overview: {
        pts_learn_more_button_click: 'solutions_generative-ai_acceleration-overview_pts-learn-more-button_click',
        build_cache_learn_more_button_click:
          'solutions_generative-ai_acceleration-overview_build-cache-learn-more-button_click',
        td_learn_more_button_click: 'solutions_generative-ai_acceleration-overview_td-learn-more-button_click',
      },
      troubleshooting_overview: {
        build_scan_learn_more_button_click:
          'solutions_generative-ai_troubleshooting-overview_build-scan-learn-more-button_click',
        failure_clustering_analytics_learn_more_button_click:
          'solutions_generative-ai_troubleshooting-overview_failure-clustering-analytics-learn-more-button_click',
        flaky_test_detection_learn_more_button_click:
          'solutions_generative-ai_troubleshooting-overview_flaky-test-detection-learn-more-button_click',
      },
      governance_overview: {
        request_more_info_button_click: 'solutions_generative-ai_governance-overview_request-more-info-button_click',
      },
      observability_overview: {
        learn_more_button_click: 'solutions_generative-ai_observability-overview_learn-more-button_click',
      },
    },
    dora: {
      hero: {
        request_trial_clicked: 'solutions_dora_hero_request-trial_clicked',
        learn_more_clicked: 'solutions_dora_hero_learn-more_clicked',
      },
      'dora-significance': {
        smaller_batches_text: 'solutions_dora_dora-significance_smaller-batches_clicked',
        continuous_testing_text: 'solutions_dora_dora-significance_continuous-testing_clicked',
        netflix_text: 'solutions_dora_dora-significance_netflix-text_clicked',
        netflix_text_2: 'solutions_dora_dora-significance_netflix-text-2_clicked',
        spotify_text: 'solutions_dora_dora-significance_spotify-text_clicked',
        revolut_text: 'solutions_dora_dora-significance_revolut-text_clicked',
        applied_materials_text: 'solutions_dora_dora-significance_applied-materials-text_clicked',
      },
      'related-features': {
        build_cache_card: 'solutions_dora_related-features_build-cache-card_clicked',
        predictive_test_selection_card: 'solutions_dora_related-features_predictive-test-selection-card_clicked',
        test_distribution_card: 'solutions_dora_related-features_test-distribution-card_clicked',
        build_scan_card: 'solutions_dora_related-features_build-scan-card_clicked',
        td_card: 'solutions_dora_related-features_td-card_clicked',
        failure_analytics_card: 'solutions_dora_related-features_failure-analytics-card_clicked',
        failure_summaries_card: 'solutions_dora_related-features_failure-summaries-card_clicked',
        flaky_test_detection_card: 'solutions_dora_related-features_flaky-test-detection-card_clicked',
        analytics_card: 'solutions_dora_related-features_analytics-card_clicked',
        software_provenance_card: 'solutions_dora_related-features_software-provenance-card_clicked',
        policy_automation_card: 'solutions_dora_related-features_policy-automation-card_clicked',
        ci_build_system_card: 'solutions_dora_related-features_ci-build-system-card_clicked',
        analytics_card_2: 'solutions_dora_related-features_analytics-card-2_clicked',
        performance_insights_card: 'solutions_dora_related-features_performance-insights-card_clicked',
        analytics_card_3: 'solutions_dora_related-features_analytics-card-3_clicked',
      },
    },
  },
} as const satisfies FathomEventStructure;

// Legacy events - these should be migrated to the structured format above and not added to.
export const LEGACY_FATHOM_EVENTS = {
  TRIAL_REQUEST: 'trial-request',
  PRICING_REQUEST: 'pricing-request',
  NEWSLETTER_FORM: 'newsletter-form',
  MARKETING_BANNER_LINK_CLICK: 'marketing-banner-link-click',
} as const;

// Type helper to extract event names
type ExtractEventNames<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: ExtractEventNames<T[K]> }[keyof T]
    : never;

// Export types for event names
export type FathomEventName = ExtractEventNames<typeof FATHOM_EVENTS>;
export type LegacyFathomEventName = (typeof LEGACY_FATHOM_EVENTS)[keyof typeof LEGACY_FATHOM_EVENTS];
export type AllFathomEventNames = FathomEventName | LegacyFathomEventName;
